import React, { useState, useRef, useEffect } from 'react'
import { Box, TextField, Typography, IconButton } from '@mui/material'
import { SentimentSatisfiedAlt as SmileIcon } from '@mui/icons-material'
import MUIEmojiPicker from './MUIEmojiPicker'

interface MUICaptionWithEmojisProps {
  captionRef: React.MutableRefObject<string>
  placeholder?: string
  maxLength?: number
  rows?: number
}

const MUICaptionWithEmojis: React.FC<MUICaptionWithEmojisProps> = ({
  captionRef,
  placeholder = "Write your caption... Don't forget the hashtags! #Instagram",
  maxLength = 2200,
  rows = 5,
}) => {
  const [caption, setCaption] = useState(captionRef.current)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [cursorPosition, setCursorPosition] = useState(0)
  const textFieldRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Keep the ref in sync with the state
  useEffect(() => {
    captionRef.current = caption
  }, [caption, captionRef])

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false)
      }
    }

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEmojiPicker])

  const handleEmojiSelect = (emoji: string) => {
    const newValue = caption.substring(0, cursorPosition) + emoji + caption.substring(cursorPosition)
    setCaption(newValue)

    // Set cursor position after the inserted emoji
    setTimeout(() => {
      if (textFieldRef.current) {
        const newPosition = cursorPosition + emoji.length
        textFieldRef.current.selectionStart = newPosition
        textFieldRef.current.selectionEnd = newPosition
        textFieldRef.current.focus()
        setCursorPosition(newPosition)
      }
    }, 0)
  }

  const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCaption(e.target.value)
    setCursorPosition(e.target.selectionStart ?? 0)
  }

  const handleCursorMove = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (textFieldRef.current) {
      setCursorPosition(textFieldRef.current.selectionStart || 0)
    }
  }

  const remainingChars = maxLength - caption.length
  const isNearLimit = remainingChars < 100
  const isOverLimit = remainingChars < 0

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        p: 3, 
        borderRadius: 2, 
        background: 'rgba(30,41,59,0.4)', 
        border: '1px solid rgba(168,85,247,0.15)',
        position: 'relative',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>
          Caption
        </Typography>
        <Typography 
          sx={{ 
            fontSize: 12, 
            fontWeight: 500,
            color: isOverLimit ? '#f87171' : isNearLimit ? '#fbbf24' : 'rgba(255,255,255,0.5)',
            transition: 'color 0.2s',
          }}
        >
          {remainingChars.toLocaleString()} characters remaining
        </Typography>
      </Box>

      <TextField
        inputRef={textFieldRef}
        multiline
        rows={rows}
        placeholder={placeholder}
        fullWidth
        value={caption}
        onChange={handleCaptionChange}
        onClick={handleCursorMove}
        onKeyUp={handleCursorMove}
        inputProps={{
          maxLength: maxLength,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
            '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
            '&.Mui-focused fieldset': { borderColor: '#a855f7' },
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
          },
          '& .MuiInputBase-input': {
            color: '#fff',
            fontSize: '15px',
          },
          '& .MuiInputBase-input::placeholder': {
            color: 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
        }}
      />

      {/* Bottom bar with emoji button and tips */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <IconButton
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          sx={{
            background: showEmojiPicker 
              ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
              : 'rgba(15, 23, 42, 0.5)',
            border: showEmojiPicker 
              ? '1px solid rgba(168, 85, 247, 0.5)'
              : '1px solid rgba(168, 85, 247, 0.3)',
            color: showEmojiPicker ? '#fff' : 'rgba(255, 255, 255, 0.6)',
            borderRadius: 2,
            px: 2,
            py: 1,
            '&:hover': {
              background: showEmojiPicker
                ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
                : 'rgba(168, 85, 247, 0.1)',
              borderColor: 'rgba(168, 85, 247, 0.5)',
            },
            transition: 'all 0.2s',
          }}
        >
          <SmileIcon sx={{ fontSize: 20, mr: 1 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            Emoji
          </Typography>
        </IconButton>

        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
          💡 Tip: Use 3-5 relevant hashtags for maximum reach
        </Typography>
      </Box>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <MUIEmojiPicker
          onEmojiSelect={handleEmojiSelect}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}
    </Box>
  )
}

export default MUICaptionWithEmojis